import React, { useEffect, useState } from 'react';
import { MenuData, MenuItem as MenuItemType, MenuCategory, validateItem } from '../types';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableItem } from './SortableItem';
import { GripVertical, Pencil, EyeOff, Check, Star, AlertCircle, Image as ImageIcon } from 'lucide-react';

interface MenuCanvasProps {
  data: MenuData;
  language: 'en' | 'ar';
  printRef: React.RefObject<HTMLDivElement>;
  onCategoryClick?: (id: string) => void;
  onItemClick?: (id: string) => void;
  focusTarget?: { id: string; ts: number } | null;
  currency: string;
  printOptions?: {
    hideImages: boolean;
    filter: 'all' | 'notes_only';
  };
  viewFilters?: {
    showImagesOnly: boolean;
    showNotesOnly: boolean;
    showIncompleteOnly: boolean;
  };
}

// --- Visual Components ---

const MenuItem = ({ item, language, currency, onClick, printOptions, viewFilters }: { 
    item: MenuItemType, 
    language: 'en' | 'ar', 
    currency: string, 
    onClick?: () => void, 
    printOptions?: MenuCanvasProps['printOptions'],
    viewFilters?: { showImagesOnly: boolean; showNotesOnly: boolean; showIncompleteOnly: boolean }
}) => {
  const isArabic = language === 'ar';
  const validationErrors = validateItem(item);
  const isIncomplete = validationErrors.length > 0;
  
  // Filter: Notes Only (Print)
  if (printOptions?.filter === 'notes_only' && !item.note?.en && !item.note?.ar) {
      return null;
  }

  // View Filters
  if (viewFilters?.showImagesOnly && (!item.image || item.imageNotAvailable)) return null;
  if (viewFilters?.showNotesOnly && !item.note?.en && !item.note?.ar) return null;
  if (viewFilters?.showIncompleteOnly && !isIncomplete) return null;

  return (
    <div 
      id={`canvas-item-${item.id}`} 
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      className={`py-6 relative group ${isArabic ? 'flex-row-reverse text-right' : 'flex-row text-left'} flex gap-6 cursor-pointer hover:bg-slate-50/50 rounded-lg transition-colors px-2 -mx-2 ${viewFilters?.showIncompleteOnly && isIncomplete ? 'bg-red-50/50 border border-red-100' : ''}`}
    >
      
      {/* Validation Errors Overlay */}
      {viewFilters?.showIncompleteOnly && isIncomplete && (
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-red-100 text-red-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-red-200 shadow-sm z-10 whitespace-nowrap flex items-center gap-1">
              <AlertCircle size={10} />
              <span>Missing: {validationErrors.join(', ')}</span>
          </div>
      )}

      {/* Item Image */}
      {!printOptions?.hideImages && (item.image || item.imageNotAvailable) && (
        <div className="shrink-0 w-32 h-32 rounded-lg overflow-hidden shadow-sm border border-slate-100 bg-slate-50 self-start flex items-center justify-center">
          {item.imageNotAvailable ? (
             <div className="text-center p-2">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Image Not Available</span>
             </div>
          ) : (
            <img 
                src={item.image} 
                alt={item.name.en} 
                className="w-full h-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Name and Price Line */}
        <div className={`flex items-baseline justify-between mb-2 ${isArabic ? 'flex-row-reverse' : 'flex-row'}`}>
            <h4 className={`text-xl font-bold text-slate-900 leading-tight ${isArabic ? 'font-arabic' : 'font-sans tracking-tight'}`}>
              {isArabic ? (item.name.ar || item.name.en) : item.name.en}
            </h4>
            
            <div className={`flex items-baseline gap-1 shrink-0 ${isArabic ? 'mr-6 flex-row-reverse' : 'ml-6'}`}>
                 <span className="text-xl font-bold text-slate-900">
                    {item.price.toFixed(2)}
                 </span>
                 <span className="text-sm font-normal text-slate-400 ml-1">{currency}</span>
            </div>
        </div>
        
        {/* Description */}
        <p className={`text-sm text-slate-600 leading-relaxed max-w-prose ${isArabic ? 'font-arabic' : 'font-serif italic'}`}>
        {isArabic ? (item.description.ar || item.description.en) : item.description.en}
        </p>

        {/* Item Note Chip */}
        {item.note && (item.note.en || item.note.ar) && (
            <div className={`mt-2 ${isArabic ? 'text-right' : 'text-left'}`}>
                <span className="inline-block bg-yellow-100 text-yellow-800 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide border border-yellow-200">
                    {isArabic ? (item.note.ar || item.note.en) : item.note.en}
                </span>
            </div>
        )}

        {/* Choice Groups Display */}
        {item.choiceGroups.length > 0 && (
            <div className="mt-5 space-y-4">
                {item.choiceGroups.map(group => (
                    <div key={group.id} className="relative">
                        {/* Group Header */}
                        <div className={`flex items-center gap-3 mb-2 ${isArabic ? 'flex-row-reverse' : 'flex-row'}`}>
                            <h5 className={`text-[10px] font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap ${isArabic ? 'font-arabic' : 'font-sans'}`}>
                                {isArabic ? (group.name.ar || group.name.en) : group.name.en}
                            </h5>
                            <div className="h-px bg-slate-100 w-16"></div>
                        </div>
                        
                        {/* Options List */}
                        <div className="space-y-1.5">
                            {group.options.map((opt) => {
                                const name = isArabic ? (opt.name.ar || opt.name.en) : opt.name.en;
                                return (
                                    <div key={opt.id} className={`flex items-center gap-3 text-sm text-slate-700 ${isArabic ? 'flex-row-reverse' : 'flex-row'}`}>
                                        <div className={`w-1 h-1 rounded-full bg-slate-300 shrink-0`}></div>
                                        <span className={`flex-1 ${isArabic ? 'font-arabic' : 'font-sans'}`}>
                                            {name}
                                        </span>
                                        {opt.priceModifier ? (
                                            <span className="text-xs font-semibold text-slate-500 whitespace-nowrap bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">
                                                +{currency} {opt.priceModifier.toFixed(2)}
                                            </span>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        )}
      </div>

      {/* Drag Handle Overlay */}
      <div className={`absolute top-6 opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing text-slate-300 print:hidden transition-opacity ${isArabic ? '-right-10' : '-left-10'}`}>
        <GripVertical size={24} />
      </div>
    </div>
  );
};

const CategoryBlock = ({ category, language, onClick, onItemClick, currency, printOptions, viewFilters }: { 
    category: MenuCategory, 
    language: 'en' | 'ar', 
    onClick?: () => void, 
    onItemClick?: (id: string) => void, 
    currency: string, 
    printOptions?: MenuCanvasProps['printOptions'],
    viewFilters?: { showImagesOnly: boolean; showNotesOnly: boolean; showIncompleteOnly: boolean }
}) => {
  const isArabic = language === 'ar';

  // Filter: Notes Only - Hide category if no items match (Print)
  if (printOptions?.filter === 'notes_only') {
      const hasMatchingItems = category.items.some(i => i.note?.en || i.note?.ar);
      if (!hasMatchingItems) return null;
  }

  // View Filters Logic for Category Visibility
  if (viewFilters) {
      const hasMatchingItems = category.items.some(item => {
          if (viewFilters.showImagesOnly && (!item.image || item.imageNotAvailable)) return false;
          if (viewFilters.showNotesOnly && !item.note?.en && !item.note?.ar) return false;
          if (viewFilters.showIncompleteOnly && validateItem(item).length === 0) return false;
          return true;
      });
      if (!hasMatchingItems) return null;
  }

  return (
    <div 
        id={`canvas-cat-${category.id}`}
        className="mb-12 break-inside-avoid relative group"
    >
       {/* Category Title */}
       <div 
          onClick={onClick}
          className={`flex items-center mb-8 pb-3 border-b-2 border-slate-900 cursor-pointer hover:bg-slate-50/50 rounded transition-colors ${isArabic ? 'flex-row-reverse' : 'flex-row'}`}
          title="Click to edit category"
       >
         <h3 className={`text-2xl font-black uppercase tracking-widest text-slate-900 flex-1 ${isArabic ? 'font-arabic text-right' : 'font-sans text-left'}`}>
            {isArabic ? (category.name.ar || category.name.en) : category.name.en}
         </h3>
         
         <div className={`opacity-0 group-hover:opacity-100 text-slate-400 px-3 print:hidden transition-opacity`}>
             <Pencil size={16} />
         </div>

         <div className={`absolute top-2 opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing text-slate-300 print:hidden ${isArabic ? '-right-10' : '-left-10'}`}>
            <GripVertical size={24} />
         </div>
       </div>

       <div className="space-y-4">
         <SortableContext 
            id={category.id} 
            items={category.items.map(i => i.id)} 
            strategy={verticalListSortingStrategy}
         >
            {category.items.length === 0 && (
                <div className="text-center text-slate-300 text-sm py-8 italic border border-dashed border-slate-200 rounded print:hidden bg-slate-50">
                    This section is empty. Drag items here.
                </div>
            )}
            {category.items.map(item => (
                <SortableItem key={item.id} id={item.id}>
                    <MenuItem 
                        item={item} 
                        language={language} 
                        currency={currency} 
                        onClick={() => onItemClick?.(item.id)}
                        printOptions={printOptions}
                        viewFilters={viewFilters}
                    />
                </SortableItem>
            ))}
         </SortableContext>
       </div>
    </div>
  );
};

export const MenuCanvas: React.FC<MenuCanvasProps> = ({ data, language, printRef, onCategoryClick, onItemClick, focusTarget, currency, printOptions, viewFilters: propViewFilters }) => {
  const isArabic = language === 'ar';
  
  const viewFilters = propViewFilters || { showImagesOnly: false, showNotesOnly: false, showIncompleteOnly: false };

  useEffect(() => {
    if (focusTarget) {
      // Try to find item first
      const itemEl = document.getElementById(`canvas-item-${focusTarget.id}`);
      if (itemEl) {
          itemEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          itemEl.classList.add('bg-yellow-50/50');
          setTimeout(() => itemEl.classList.remove('bg-yellow-50/50'), 2000);
          return;
      }

      // Fallback to category
      const catEl = document.getElementById(`canvas-cat-${focusTarget.id}`);
      if (catEl) {
          catEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [focusTarget]);

  return (
    <div className="h-full bg-slate-100 overflow-y-auto no-scrollbar relative">
      
      <div 
        id="printable-canvas"
        ref={printRef}
        className={`min-h-full bg-white p-8 md:p-12 lg:p-16 flex flex-col transition-all duration-300 ${isArabic ? 'dir-rtl' : 'dir-ltr'} relative`}
        dir={isArabic ? 'rtl' : 'ltr'}
      >
        <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{backgroundImage: 'url("https://www.transparenttextures.com/patterns/cream-paper.png")'}}></div>

        <div className="max-w-5xl mx-auto w-full flex-1 flex flex-col">
            <header className="text-center mb-16 relative z-10 flex flex-col items-center">
                {data.logo && (
                    <div className="mb-6 w-32 h-32 relative">
                        <img src={data.logo} alt="Logo" className="w-full h-full object-contain" />
                    </div>
                )}
                <h1 className={`text-6xl font-black text-slate-900 mb-6 uppercase tracking-[0.2em] ${isArabic ? 'font-arabic' : 'font-sans'}`}>
                    {isArabic ? (data.title.ar || data.title.en) : data.title.en}
                </h1>
                <div className="w-24 h-2 bg-slate-900 mx-auto"></div>
            </header>

            <div className="flex-1 relative z-10">
                <SortableContext 
                    items={(data.categories || []).map(c => c.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {(data.categories || []).map(category => (
                        <SortableItem key={category.id} id={category.id}>
                            <CategoryBlock 
                                category={category} 
                                language={language} 
                                onClick={() => onCategoryClick && onCategoryClick(category.id)}
                                onItemClick={onItemClick}
                                currency={currency}
                                printOptions={printOptions}
                                viewFilters={viewFilters}
                            />
                        </SortableItem>
                    ))}
                </SortableContext>
            </div>

            <footer className="mt-20 text-center text-slate-300 text-xs uppercase tracking-widest relative z-10 pb-8">
                <p className={isArabic ? 'font-arabic' : 'font-sans'}>
                    {isArabic ? 'تم الإنشاء بواسطة إتش دي إم بيلدر' : 'Created with HDM Builder'}
                </p>
            </footer>
        </div>
      </div>
    </div>
  );
};